import React, { useEffect, useRef, useState } from 'react';
import { Combobox, TextInput, Group, Text, Loader, useCombobox, ActionIcon } from '@mantine/core';
import { MapPin, ChevronDown, X } from 'lucide-react';

export default function GooglePlacesMultiSelect({ value = [], onChange, error, label }) {
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const serviceRef = useRef(null);
  const detailsServiceRef = useRef(null);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch(''); // Clear search string when closing
    },
  });

  // Initialize Google Maps API Services
  useEffect(() => {
    if (window.google && !serviceRef.current) {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
      detailsServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    }
  }, []);

  const handleSearchChange = (val) => {
    setSearch(val);
    
    if (!val.trim() || !serviceRef.current) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    serviceRef.current.getPlacePredictions(
      { input: val },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
        setLoading(false);
      }
    );
  };

  const handleSelect = (placeId, description) => {
    const isAlreadySelected = value.some((item) => item.place_id === placeId);
    if (isAlreadySelected) return;

    detailsServiceRef.current.getDetails(
      { placeId, fields: ['address_components', 'name'] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          
          // Extract exact country and state ISO codes
          const country = place.address_components.find((c) => c.types.includes('country'));
          const state = place.address_components.find((c) => c.types.includes('administrative_area_level_1'));

          const newPlace = {
            place_id: placeId,
            name: place.name,
            description: description,
            countryIsoCode: country?.short_name || '',
            stateIsoCode: state?.short_name || '', // Falls back to '' if no state exists
          };

          onChange([...value, newPlace]);
        }
      }
    );

    setSearch('');
    setSuggestions([]);
    // Do not close combobox so user can select multiple
  };

  const handleRemove = (placeId, event) => {
    event.stopPropagation();
    onChange(value.filter((item) => item.place_id !== placeId));
  };

  // When dropdown is closed, show "India, United States". When open, show typing search string.
  const displayValue = combobox.dropdownOpened 
    ? search 
    : value.map((v) => v.name).join(', ');

  return (
    <Combobox store={combobox} onOptionSubmit={() => {}} transitionProps={{ transition: 'slide-up', duration: 50 }}>
      <Combobox.Target>
        <TextInput
          label={label}
          error={error}
          value={displayValue}
          placeholder={value.length === 0 ? "Search destination" : ""}
          onChange={(event) => {
            handleSearchChange(event.currentTarget.value);
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          leftSection={<MapPin size={18} color={combobox.dropdownOpened ? "#0056b3" : "#6c757d"} />}
          rightSection={
            <ActionIcon variant="transparent" onClick={() => combobox.toggleDropdown()}>
              <ChevronDown size={18} color="#6c757d" />
            </ActionIcon>
          }
          radius="md"
          size="md"
          styles={{
            label: { 
              color: combobox.dropdownOpened ? '#0056b3' : '#1a2a44', 
              fontWeight: 700 
            },
            input: {
              borderColor: combobox.dropdownOpened ? '#0056b3' : '#ced4da'
            }
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown p={0}>
        
        {/* TOP ROW: Selected Pills (Matches image_f03c0b.png) */}
        {value.length > 0 && (
          <Group p="sm" gap="xs" style={{ borderBottom: '1px solid #eaeaea' }}>
            {value.map((item) => (
              <Group 
                key={item.place_id} 
                gap={4}
                style={{ 
                  backgroundColor: '#0056b3', 
                  color: 'white', 
                  padding: '4px 10px', 
                  borderRadius: '16px',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                {item.name}
                <X 
                  size={14} 
                  style={{ cursor: 'pointer', marginLeft: '4px' }} 
                  onClick={(e) => handleRemove(item.place_id, e)} 
                />
              </Group>
            ))}
          </Group>
        )}

        {/* BOTTOM ROW: Suggestions */}
        <Combobox.Options mah={250} style={{ overflowY: 'auto' }}>
          {loading ? (
            <Combobox.Empty><Loader size="sm" type="dots" /></Combobox.Empty>
          ) : suggestions.length > 0 ? (
            suggestions.map((item) => (
              <Combobox.Option 
                value={item.place_id} 
                key={item.place_id} 
                onClick={() => handleSelect(item.place_id, item.description)}
                disabled={value.some((val) => val.place_id === item.place_id)}
              >
                <Group gap="sm" align="flex-start" wrap="nowrap">
                  <MapPin size={18} color="#adb5bd" style={{ marginTop: '2px' }} />
                  <div>
                    <Text size="sm" fw={500}>
                      {item.structured_formatting?.main_text || item.description.split(',')[0]}
                    </Text>
                    {item.structured_formatting?.secondary_text && (
                      <Text size="xs" c="dimmed">
                        {item.structured_formatting.secondary_text}
                      </Text>
                    )}
                  </div>
                </Group>
              </Combobox.Option>
            ))
          ) : (
            <Combobox.Empty>
              <Text size="xs" bg="#e7eff7" p="xs" style={{ borderRadius: '4px', margin: '8px' }}>
                Search for and select all destinations for your trip (e.g. resort name, point of interest, address, countries)
              </Text>
            </Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}